var __defProp = Object.defineProperty;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __assign = Object.assign;
var __name = (target, value) => __defProp(target, "name", {value, configurable: true});
var __rest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/renderComponent.ts
import {createElement} from "react";
import invariant from "tiny-invariant";
var renderComponent = /* @__PURE__ */ __name((_a) => {
  var {
    as,
    children,
    bag,
    elementProps = bag,
    childrenBag = bag
  } = _a, other = __rest(_a, [
    "as",
    "children",
    "bag",
    "elementProps",
    "childrenBag"
  ]);
  if (typeof children === "function") {
    return children(childrenBag);
  }
  if (typeof as !== "string" && as) {
    return createElement(as, __assign(__assign({}, bag), other), children);
  }
  if (as !== void 0) {
    return createElement(as, __assign(__assign({}, elementProps), other), children);
  }
  invariant(false, 'Cannot render: not specified renderer("children" or "as" props)');
}, "renderComponent");

// src/plugin.ts
import {useCallback} from "react";
var domPlugin = {
  token: Symbol.for("dom"),
  useDecorator: (shared) => {
    const {setFieldValue, setFieldTouched} = shared;
    const handleChange = useCallback((e) => {
      setFieldValue(e.target.name, e.target.value);
    }, [setFieldValue]);
    const handleBlur = useCallback((e) => {
      setFieldTouched(e.target.name, {
        $touched: true
      });
    }, [setFieldTouched]);
    return __assign(__assign({}, shared), {
      handleChange,
      handleBlur
    });
  }
};

// src/useTextField.ts
import {useFormContext, usePluginAssertion} from "@reactive-forms/core";
import {useStockValue} from "stocked";
var useTextField = /* @__PURE__ */ __name(({name}) => {
  usePluginAssertion(domPlugin, "Dom plugin is required to use text field");
  const {handleChange, handleBlur, values} = useFormContext();
  const value = useStockValue(name, values);
  return {
    onChange: handleChange,
    onBlur: handleBlur,
    name,
    value
  };
}, "useTextField");

// src/TextField.tsx
var TextField = /* @__PURE__ */ __name((_a) => {
  var {
    name,
    as
  } = _a, renderComponentProps = __rest(_a, [
    "name",
    "as"
  ]);
  const bag = useTextField({name});
  return renderComponent(__assign({
    bag,
    as: as != null ? as : "input"
  }, renderComponentProps));
}, "TextField");

// src/SubmitButton.tsx
import {useCallback as useCallback2} from "react";
import {useFormContext as useFormContext2, useFormMeta} from "@reactive-forms/core";
var SubmitButton = /* @__PURE__ */ __name((_a) => {
  var {
    submitAction,
    as
  } = _a, other = __rest(_a, [
    "submitAction",
    "as"
  ]);
  const {submit} = useFormContext2();
  const isSubmitting = useFormMeta("isSubmitting");
  const onClick = useCallback2(() => {
    submit(submitAction);
  }, [submit, submitAction]);
  return renderComponent(__assign({
    as: as != null ? as : "button",
    bag: {
      onClick,
      disabled: isSubmitting
    }
  }, other));
}, "SubmitButton");

// src/Form.tsx
import React2, {useCallback as useCallback3} from "react";
import {useFormContext as useFormContext3} from "@reactive-forms/core";
var Form = /* @__PURE__ */ __name((_a) => {
  var {children, submitAction} = _a, other = __rest(_a, ["children", "submitAction"]);
  const {submit, resetForm} = useFormContext3();
  const onSubmit = useCallback3((e) => {
    e.preventDefault();
    submit(submitAction);
  }, [submit, submitAction]);
  const onReset = useCallback3(() => {
    resetForm();
  }, [resetForm]);
  return /* @__PURE__ */ React2.createElement("form", __assign({
    onSubmit,
    onReset
  }, other), children);
}, "Form");

// src/ErrorMessage.tsx
import {useFieldError, useFieldTouched} from "@reactive-forms/core";
var ErrorMessage = /* @__PURE__ */ __name((_a) => {
  var {
    name,
    as,
    children
  } = _a, renderComponentProps = __rest(_a, [
    "name",
    "as",
    "children"
  ]);
  const [touched] = useFieldTouched(name);
  const [error] = useFieldError(name);
  const message = (touched == null ? void 0 : touched.$touched) && (error == null ? void 0 : error.$error) ? error.$error : void 0;
  return message !== void 0 ? renderComponent(__assign({
    bag: {
      children: message
    },
    as: as != null ? as : "span",
    children: children != null ? children : message
  }, renderComponentProps)) : null;
}, "ErrorMessage");

// src/FieldArray.tsx
import {useArrayField} from "@reactive-forms/core";
var FieldArray = /* @__PURE__ */ __name((_a) => {
  var {
    name
  } = _a, renderComponentProps = __rest(_a, [
    "name"
  ]);
  const bag = useArrayField({name});
  return renderComponent(__assign({
    bag
  }, renderComponentProps));
}, "FieldArray");

// src/FieldValue.tsx
import React3, {Fragment} from "react";
import {useFieldValue} from "@reactive-forms/core";
var DefaultFieldValueRenderer = /* @__PURE__ */ __name(({children}) => /* @__PURE__ */ React3.createElement(Fragment, null, children), "DefaultFieldValueRenderer");
var FieldValue = /* @__PURE__ */ __name((_a) => {
  var {
    name,
    children
  } = _a, renderComponentProps = __rest(_a, [
    "name",
    "children"
  ]);
  const [value] = useFieldValue(name);
  return renderComponent(__assign({
    bag: {
      children: value
    },
    childrenBag: value,
    as: DefaultFieldValueRenderer,
    children: children != null ? children : value
  }, renderComponentProps));
}, "FieldValue");

// src/FieldValueArray.tsx
import React4 from "react";
import {useFieldValueArray} from "@reactive-forms/core";
var FieldValueArray = /* @__PURE__ */ __name((_a) => {
  var {children} = _a, paths = __rest(_a, ["children"]);
  const bag = useFieldValueArray(paths);
  return /* @__PURE__ */ React4.createElement(React4.Fragment, null, children(bag));
}, "FieldValueArray");
export {
  ErrorMessage,
  FieldArray,
  FieldValue,
  FieldValueArray,
  Form,
  SubmitButton,
  TextField,
  domPlugin,
  renderComponent,
  useTextField
};
